import '@glints/poppins';
// Ordering is important here. We need to load the debug utils before any other imports
import './common/apm';
import './debugUtils';
import './lazysizes';
import './modules/AppLanguage/initializeAppLanguage';
import './polyfills';

import React, { lazy, Suspense } from 'react';
import { ThumborProvider } from '@glints/fast-image';
import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import enUS from 'antd/lib/locale-provider/en_US';
import { ConnectedRouter } from 'connected-react-router';
import {
  AlertProvider,
  AlertWithProvider,
  ModalProvider,
  ModalWithProvider,
} from 'glints-aries/es/@next';
import { parse, stringify } from 'query-string';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Route, Switch } from 'react-router';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';

import { ApolloConfig } from './apollo-client';
import { App } from './app';
import { ActionHintQueueProvider } from './common/ActionHintQueueProvider';
import { AutoModalProvider } from './common/AutoModalProvider';
import { setDeviceIDToCookie } from './common/device-cookie';
import { CompanyVerificationModalsProvider } from './components/CompanyVerificationModals/CompanyVerificationModalsProvider';
import ErrorBoundaryWithCrashReporting from './components/Error/ErrorBoundaryWithCrashReporting';
import LoadingFullscreen from './components/LoadingFullscreen';
import { UpdateCompanyVerificationModalProvider } from './components/Modal/CompanyTierVerificationModal/hooks/useUpdateCompanyVerificationModal';
import SessionInitializer from './components/SessionInitializer';
import ActivateFreeTrialModalProvider from './components/VIPFreeTrialModal/ActivateFreeTrialModalProvider';
import config from './config';
import IntlProvider from './IntlProvider';
import ContactPacksPurchaseModalProvider from './modules/CreditSystem/PurchaseModals/ContactPacksPurchaseModal/Providers/ContactPacksPurchaseModalProvider';
import { GlintsVIPPurchaseModalProvider } from './modules/CreditSystem/PurchaseModals/GlintsVIPPurchaseModal/Providers/GlintsVIPPurchaseModalProvider';
import PremiumPlanPurchaseModalProvider from './modules/CreditSystem/PurchaseModals/PremiumPlanPurchaseModal/Providers/PremiumPlanPurchaseModalProvider';
import V2CreditProductPurchaseProcessingHandler from './modules/CreditSystem/PurchaseModals/V2CreditProductPurchaseProcessingHandler/V2CreditProductPurchaseProcessingHandler';
import { SubscriptionAssistanceRequestModalProvider } from './modules/EmployerCommerce/SubscriptionPlanUpgrade/SubscriptionAssistanceRequestModal/SubscriptionAssistanceRequestModalProvider';
import { Error500Page } from './modules/ErrorPages';
import FeatureFlagProvider from './modules/FeatureFlag/FeatureFlagProvider';
import { initializeGlintsTrackingClient } from './modules/GlintsTracking/initializeGlintsTrackingClient';
import RenderlessInitializeGlintsTracking from './modules/GlintsTracking/RenderlessInitializeGlintsTracking';
import HideModalsOnMobileAppPrompt from './modules/UseMobileAppPrompt/HideModalsOnMobileAppPrompt';
import { reactQueryClient } from './react-query-client';
import store, { browserHistory } from './store';
import { SWRConfig } from './swr';

const VIPPurchaseSuccessAlert = lazy(
  () =>
    import('./modules/CreditSystem/SharedComponents/VIPPurchaseSuccessAlert')
);

async function renderApp() {
  const container = document.getElementById('app');
  const root = createRoot(container as Element);

  setDeviceIDToCookie();
  // Tracking must be ready before route components emit their initial page views.
  initializeGlintsTrackingClient();

  root.render(
    <Provider store={store}>
      <IntlProvider>
        <ErrorBoundaryWithCrashReporting
          fallback={<Error500Page />}
          transactionName="global-error-boundary"
        >
          {/* @ts-ignore */}
          <ThumborProvider thumborServerURL={config.THUMBOR_BASE}>
            <ApolloConfig>
              <QueryClientProvider client={reactQueryClient}>
                <SWRConfig store={store}>
                  <FeatureFlagProvider>
                    {/* @ts-ignore */}
                    <ConnectedRouter history={browserHistory}>
                      <CompatRouter>
                        <QueryParamProvider
                          adapter={ReactRouter5Adapter}
                          options={{
                            searchStringToObject: parse,
                            objectToSearchString: stringify,
                          }}
                        >
                          <Switch>
                            <Route>
                              <ConfigProvider locale={enUS}>
                                <div className="app">
                                  <SessionInitializer>
                                    <HideModalsOnMobileAppPrompt />
                                    <AlertProvider>
                                      <AlertWithProvider />
                                      <Suspense
                                        fallback={<LoadingFullscreen />}
                                      >
                                        <AutoModalProvider>
                                          <ActionHintQueueProvider>
                                            <CompanyVerificationModalsProvider>
                                              <ModalProvider>
                                                <SubscriptionAssistanceRequestModalProvider>
                                                  <PremiumPlanPurchaseModalProvider>
                                                    <UpdateCompanyVerificationModalProvider>
                                                      <ModalWithProvider />
                                                      <ContactPacksPurchaseModalProvider>
                                                        <ActivateFreeTrialModalProvider>
                                                          <GlintsVIPPurchaseModalProvider>
                                                            <V2CreditProductPurchaseProcessingHandler />
                                                            <ErrorBoundaryWithCrashReporting
                                                              fallback={null}
                                                            >
                                                              <Suspense
                                                                fallback={null}
                                                              >
                                                                <VIPPurchaseSuccessAlert />
                                                              </Suspense>
                                                            </ErrorBoundaryWithCrashReporting>
                                                            <RenderlessInitializeGlintsTracking />
                                                            <Suspense
                                                              fallback={
                                                                <LoadingFullscreen />
                                                              }
                                                            >
                                                              <App />
                                                            </Suspense>
                                                          </GlintsVIPPurchaseModalProvider>
                                                        </ActivateFreeTrialModalProvider>
                                                      </ContactPacksPurchaseModalProvider>
                                                    </UpdateCompanyVerificationModalProvider>
                                                  </PremiumPlanPurchaseModalProvider>
                                                </SubscriptionAssistanceRequestModalProvider>
                                              </ModalProvider>
                                            </CompanyVerificationModalsProvider>
                                          </ActionHintQueueProvider>
                                        </AutoModalProvider>
                                      </Suspense>
                                    </AlertProvider>
                                  </SessionInitializer>
                                </div>
                              </ConfigProvider>
                            </Route>
                          </Switch>
                        </QueryParamProvider>
                      </CompatRouter>
                    </ConnectedRouter>
                  </FeatureFlagProvider>
                </SWRConfig>
              </QueryClientProvider>
            </ApolloConfig>
          </ThumborProvider>
        </ErrorBoundaryWithCrashReporting>
      </IntlProvider>
    </Provider>
  );
}

renderApp();
